import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import { marketingApi, API_BASE } from "../../services/api";
import { useToast } from "../../contexts/ToastContext";
import "./Marketing.css";

// ─── Add Template Modal ───────────────────────────────────────────────────────

function AddTemplateModal({ open, onClose, onSuccess }) {
  const [form, setForm] = useState({
    key: "", name: "", templateName: "", language: "en_US", description: "",
  });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const field = (f) => (e) => setForm((prev) => ({ ...prev, [f]: e.target.value }));

  const handleClose = () => {
    if (saving) return;
    setForm({ key: "", name: "", templateName: "", language: "en_US", description: "" });
    setError("");
    onClose();
  };

  const handleSubmit = async () => {
    if (!form.key.trim() || !form.name.trim() || !form.templateName.trim() || !form.language.trim()) {
      setError("Key, Display Name, Template Name and Language are required.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const data = await marketingApi.addTemplate({
        key: form.key.trim(),
        name: form.name.trim(),
        templateName: form.templateName.trim(),
        language: form.language.trim(),
        description: form.description.trim(),
      });
      if (data.success === false) throw new Error(data.error || "Failed to add template");
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Failed to add template");
    }
    setSaving(false);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: "12px" } }}
    >
      <DialogTitle sx={{ fontWeight: 600, fontSize: "17px" }}>Add Template</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px !important" }}>
        {error && <div className="mkt-inline-error">{error}</div>}
        <TextField
          label="Key *"
          size="small"
          fullWidth
          autoFocus
          value={form.key}
          onChange={field("key")}
          helperText="Lowercase letters, numbers and underscores only. e.g. summer_caps_2026"
        />
        <TextField
          label="Display Name *"
          size="small"
          fullWidth
          value={form.name}
          onChange={field("name")}
          helperText="Shown in the dropdown"
        />
        <TextField
          label="Template Name *"
          size="small"
          fullWidth
          value={form.templateName}
          onChange={field("templateName")}
          helperText="Exact name from Meta Business Manager e.g. summer_caps_promo"
        />
        <TextField
          label="Language *"
          size="small"
          fullWidth
          value={form.language}
          onChange={field("language")}
          helperText="e.g. en_US"
        />
        <TextField
          label="Description"
          size="small"
          fullWidth
          value={form.description}
          onChange={field("description")}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button
          onClick={handleClose}
          disabled={saving}
          sx={{ color: "#54656f", textTransform: "none" }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving}
          sx={{
            bgcolor: "#075e54",
            "&:hover": { bgcolor: "#054d44" },
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          {saving ? "Adding…" : "Add Template"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Delete Confirmation Dialog ───────────────────────────────────────────────

function DeleteConfirmDialog({ open, onClose, onConfirm, loading }) {
  return (
    <Dialog
      open={open}
      onClose={() => !loading && onClose()}
      PaperProps={{ sx: { borderRadius: "12px", minWidth: 320 } }}
    >
      <DialogTitle sx={{ fontWeight: 600, fontSize: "17px" }}>Delete Template?</DialogTitle>
      <DialogContent>
        <p style={{ fontSize: "14px", color: "#667781", margin: 0 }}>
          This template will be permanently removed and can no longer be used for broadcasts.
        </p>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button
          onClick={onClose}
          disabled={loading}
          sx={{ color: "#54656f", textTransform: "none" }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={loading}
          sx={{
            bgcolor: "#d9534f",
            "&:hover": { bgcolor: "#c9302c" },
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          {loading ? "Deleting…" : "Delete"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(str) {
  if (!str) return "—";
  try {
    return new Date(str).toLocaleDateString([], {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch {
    return str;
  }
}

// ─── Marketing Page ───────────────────────────────────────────────────────────

function Marketing() {
  const showToast = useToast();

  // ── Shared template state ──────────────────────────────────────────────────
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [templatesError, setTemplatesError] = useState(null);

  // ── Template management ────────────────────────────────────────────────────
  const [addOpen, setAddOpen] = useState(false);
  const [deleteKey, setDeleteKey] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // ── Broadcast Send ─────────────────────────────────────────────────────────
  const [selectedKey, setSelectedKey] = useState("");
  const [phoneInput, setPhoneInput] = useState("");
  // streamStatus: null | 'starting' | 'streaming' | 'done' | 'error'
  const [streamStatus, setStreamStatus] = useState(null);
  const [streamTotal, setStreamTotal] = useState(0);
  const [streamResults, setStreamResults] = useState([]);
  const [sentCount, setSentCount] = useState(0);
  const [failedCount, setFailedCount] = useState(0);
  const [sendError, setSendError] = useState("");
  const esRef = useRef(null);
  const resultsListRef = useRef(null);

  // ── Load templates ─────────────────────────────────────────────────────────
  const loadTemplates = useCallback(async () => {
    setTemplatesLoading(true);
    setTemplatesError(null);
    try {
      const data = await marketingApi.getTemplates();
      setTemplates(Array.isArray(data) ? data : data.templates || []);
    } catch (err) {
      setTemplatesError(err.response?.data?.error || err.message || "Failed to load templates");
    }
    setTemplatesLoading(false);
  }, []);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  // ── Delete ─────────────────────────────────────────────────────────────────
  const handleDeleteConfirm = async () => {
    if (!deleteKey) return;
    setDeleteLoading(true);
    try {
      const data = await marketingApi.deleteTemplate(deleteKey);
      if (data.success === false) throw new Error(data.error || "Failed to delete");
      if (selectedKey === deleteKey) setSelectedKey("");
      setDeleteKey(null);
      loadTemplates();
    } catch (err) {
      showToast(err.response?.data?.error || err.message || "Failed to delete template", "error");
      setDeleteKey(null);
    }
    setDeleteLoading(false);
  };

  // ── Parsed phones (derived) ────────────────────────────────────────────────
  const parsedPhones = useMemo(
    () => phoneInput.split(/[\n,]+/).map((p) => p.trim()).filter(Boolean),
    [phoneInput]
  );

  // ── EventSource cleanup on unmount ────────────────────────────────────────
  useEffect(() => {
    return () => { esRef.current?.close(); };
  }, []);

  // ── Auto-scroll results list as new rows arrive ────────────────────────────
  useEffect(() => {
    if (resultsListRef.current) {
      resultsListRef.current.scrollTop = resultsListRef.current.scrollHeight;
    }
  }, [streamResults.length]);

  // ── Start broadcast ────────────────────────────────────────────────────────
  const handleSend = async () => {
    esRef.current?.close();
    esRef.current = null;

    setStreamStatus("starting");
    setStreamResults([]);
    setSentCount(0);
    setFailedCount(0);
    setSendError("");

    let jobId, total;
    try {
      const data = await marketingApi.startBulk(selectedKey, parsedPhones);
      if (data.success === false) throw new Error(data.error || "Failed to start broadcast");
      jobId = data.jobId;
      total = data.total ?? parsedPhones.length;
      setStreamTotal(total);
    } catch (err) {
      setSendError(err.response?.data?.error || err.message || "Failed to start broadcast");
      setStreamStatus(null);
      return;
    }

    setStreamStatus("streaming");

    const es = new EventSource(
      `${API_BASE}/admin/api/marketing/send-bulk/stream/${jobId}`,
      { withCredentials: true }
    );
    esRef.current = es;

    es.addEventListener("progress", (e) => {
      try {
        const result = JSON.parse(e.data);
        setStreamResults((prev) => [...prev, result]);
        if (result.success) {
          setSentCount((n) => n + 1);
        } else {
          setFailedCount((n) => n + 1);
        }
      } catch {}
    });

    es.addEventListener("done", () => {
      es.close();
      esRef.current = null;
      setStreamStatus("done");
    });

    es.onerror = () => {
      es.close();
      esRef.current = null;
      setStreamStatus("error");
      setSendError("Connection lost. Refresh to check final status.");
    };
  };

  const isSending = streamStatus === "starting" || streamStatus === "streaming";
  const canSend = Boolean(selectedKey) && parsedPhones.length > 0 && !isSending;

  const handleClear = () => {
    esRef.current?.close();
    esRef.current = null;
    setPhoneInput("");
    setStreamStatus(null);
    setStreamTotal(0);
    setStreamResults([]);
    setSentCount(0);
    setFailedCount(0);
    setSendError("");
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="marketing-page">
      <div className="marketing-page-header">
        <h1>Marketing</h1>
        <p className="marketing-page-subtitle">
          Manage broadcast templates and send campaigns to your customers.
        </p>
      </div>

      {/* ────── Section 1: Template Management ────── */}
      <section className="mkt-section">
        <div className="mkt-section-head">
          <div>
            <h2 className="mkt-section-title">Templates</h2>
            <p className="mkt-section-sub">WhatsApp message templates approved in Meta Business Manager.</p>
          </div>
          <button className="mkt-btn-primary" onClick={() => setAddOpen(true)}>
            + Add Template
          </button>
        </div>

        {templatesLoading ? (
          <div className="mkt-state-msg">Loading templates…</div>
        ) : templatesError ? (
          <div className="mkt-inline-error">
            {templatesError}&nbsp;
            <button className="mkt-text-btn" onClick={loadTemplates}>Retry</button>
          </div>
        ) : templates.length === 0 ? (
          <div className="mkt-state-msg">
            No templates yet. Click <strong>+ Add Template</strong> to create one.
          </div>
        ) : (
          <div className="mkt-table-wrap">
            <table className="mkt-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Template Name</th>
                  <th>Language</th>
                  <th>Description</th>
                  <th>Created At</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {templates.map((t) => (
                  <tr key={t.key}>
                    <td><span className="mkt-name">{t.name}</span></td>
                    <td><code className="mkt-mono">{t.templateName || t.template_name}</code></td>
                    <td>{t.language}</td>
                    <td className="mkt-desc">{t.description || "—"}</td>
                    <td className="mkt-date">{formatDate(t.created_at || t.createdAt)}</td>
                    <td>
                      <button
                        className="mkt-btn-delete"
                        onClick={() => setDeleteKey(t.key)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ────── Section 2: Broadcast Send ────── */}
      <section className="mkt-section">
        <div className="mkt-section-head">
          <div>
            <h2 className="mkt-section-title">Broadcast Send</h2>
            <p className="mkt-section-sub">Send a template message to multiple recipients at once.</p>
          </div>
        </div>

        <div className="mkt-broadcast-form">
          {/* Template selector */}
          <div className="mkt-field">
            <label className="mkt-label">Template</label>
            {templates.length === 0 ? (
              <p className="mkt-hint">No templates yet. Add one above.</p>
            ) : (
              <select
                className="mkt-select"
                value={selectedKey}
                onChange={(e) => setSelectedKey(e.target.value)}
              >
                <option value="" disabled>— Select Template —</option>
                {templates.map((t) => (
                  <option key={t.key} value={t.key}>{t.name}</option>
                ))}
              </select>
            )}
          </div>

          {/* Phone numbers */}
          <div className="mkt-field">
            <label className="mkt-label">Phone Numbers</label>
            <textarea
              className="mkt-phones-ta"
              value={phoneInput}
              onChange={(e) => setPhoneInput(e.target.value)}
              placeholder={"923001234567\n923001234568\n923001234569"}
              rows={6}
              spellCheck={false}
            />
            <p className="mkt-hint">
              One per line or comma-separated. Country code required, no + sign. e.g. 923001234567
            </p>
            {parsedPhones.length > 0 && (
              <p className="mkt-count">
                {parsedPhones.length} number{parsedPhones.length !== 1 ? "s" : ""} ready
              </p>
            )}
          </div>

          {/* Starting message */}
          {streamStatus === "starting" && (
            <div className="mkt-stream-starting">
              Starting broadcast to {parsedPhones.length} number{parsedPhones.length !== 1 ? "s" : ""}…
            </div>
          )}

          {/* Error */}
          {sendError && <div className="mkt-inline-error">{sendError}</div>}

          {/* Send row */}
          <div className="mkt-send-row">
            <button className="mkt-btn-send" onClick={handleSend} disabled={!canSend}>
              {isSending
                ? `Sending to ${streamTotal || parsedPhones.length} number${(streamTotal || parsedPhones.length) !== 1 ? "s" : ""}…`
                : `Send to ${parsedPhones.length} number${parsedPhones.length !== 1 ? "s" : ""}`}
            </button>
            {(streamStatus === "done" || streamStatus === "error" || sendError) && (
              <button className="mkt-btn-secondary" onClick={handleClear}>
                Clear
              </button>
            )}
          </div>

          {/* Live / final results panel */}
          {(streamStatus === "streaming" || streamStatus === "done") && (
            <div className="mkt-results">
              <div className="mkt-results-summary">
                {streamStatus === "streaming" ? (
                  <>
                    <span className="mkt-sum-live">
                      <span className="mkt-pulse" /> Sending to {streamTotal} number{streamTotal !== 1 ? "s" : ""}…
                    </span>
                    <span className="mkt-sum-ok">✅ Sent: <strong>{sentCount}</strong></span>
                    <span className="mkt-sum-fail">❌ Failed: <strong>{failedCount}</strong></span>
                    <span className="mkt-sum-remaining">
                      ⏳ Remaining: <strong>{streamTotal - sentCount - failedCount}</strong>
                    </span>
                  </>
                ) : (
                  <>
                    <span>Total: <strong>{streamTotal}</strong></span>
                    <span className="mkt-sum-ok">✅ Sent: <strong>{sentCount}</strong></span>
                    <span className="mkt-sum-fail">❌ Failed: <strong>{failedCount}</strong></span>
                  </>
                )}
              </div>
              <div className="mkt-results-list" ref={resultsListRef}>
                {streamResults.map((r, i) => (
                  <div
                    key={i}
                    className={`mkt-result-row ${r.success ? "mkt-result-ok" : "mkt-result-fail"}`}
                  >
                    {r.success ? "✅" : "❌"}&nbsp;
                    <span className="mkt-result-phone">{r.phone}</span>
                    <span className="mkt-result-sep"> — </span>
                    <span className="mkt-result-msg">
                      {r.success ? "Sent" : (r.error || "Failed")}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ────── Dialogs ────── */}
      <AddTemplateModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSuccess={() => { setAddOpen(false); loadTemplates(); }}
      />
      <DeleteConfirmDialog
        open={Boolean(deleteKey)}
        onClose={() => setDeleteKey(null)}
        onConfirm={handleDeleteConfirm}
        loading={deleteLoading}
      />
    </div>
  );
}

export default Marketing;
